<template>
  <g class="DonutChart">
    <datalas-arc
      v-for="(path, i) in paths"
      :key="i"
      :transform="`translate(${innerLeft}, ${innerTop})`"
      :fill="path.fill"
      :datum="path.datum"
      :d="path.d"
      @datum:mouseover="$emit('datum:mouseover', $event)"
      @datum:mouseout="$emit('datum:mouseout', $event)"
      @datum:click="$emit('datum:click', $event)"
    />

    <datalas-labels
      v-if="showLabels"
      :labels="labels"
      :prefix="labelPrefix"
      :suffix="labelSuffix"
      :format="labelFormat"
      :font-size="labelFontSize"
      :detect-collisions="noLabelCollisions"
      :filters="labelFilters"
      :offset-x="innerLeft"
      :offset-y="innerTop"
      style="pointer-events: none"
    />
  </g>
</template>

<script>
import Layer from './layer.vue';
import DatalasArc from '../datum/arc.vue';

export default {
  name: 'DatalasDonut',

  extends: Layer,

  components: {
    DatalasArc,
  },

  props: {
    donutSize: {
      type: Number,
      default: 1,
    },

    holeSize: {
      type: Number,
      default: 0.5,
    },

    dimensions: {
      type: Array,
      required: true,
    },
  },

  computed: {
    diameter() {
      return Math.min(this.chartWidth, this.chartHeight) * this.donutSize - 1;
    },

    outerRadius() {
      return this.diameter / 2;
    },

    innerRadius() {
      return this.outerRadius * this.holeSize;
    },

    innerLeft() {
      return this.chartWidth / 2;
    },

    innerTop() {
      return this.chartHeight / 2;
    },

    pie() {
      return this.$d3
        .pie()
        .sort(null)
        .value((d) => this.valueAccessor(d))(this.data);
    },

    makeArc() {
      return this.$d3
        .arc()
        .outerRadius(this.outerRadius)
        .innerRadius(this.innerRadius);
    },

    paths() {
      return this.pie.map((arc) => ({
        d: this.makeArc(arc),
        fill: this.fillScale(this.fillAccessor(arc.data)),
        datum: arc.data,
      }));
    },

    labels() {
      return this.pie
        .map((arc) => ({ arc, centroid: this.makeArc.centroid(arc) }))
        .map(({ arc, centroid: [x, y] }) => ({
          x,
          y,
          value: this.valueAccessor(arc.data),
          textAnchor: this.labelAnchorVal,
          dominantBaseline: this.labelBaselineVal,
          datum: arc.data,
        }));
    },

    fillTransform() {
      return this.getDimensionTransform('fill');
    },

    fillScale() {
      const domain =
        this.fillTransform.options.type === 'scheme'
          ? Object.keys(
              this.data.reduce(
                (acc, d) => ({
                  ...acc,
                  [this.fillAccessor(d)]: null,
                }),
                {},
              ),
            )
          : undefined;

      return this.fillTransform.transformer(domain);
    },
  },

  methods: {
    valueAccessor(item) {
      return item[this.getDimensionTransform('magnitude').field];
    },

    fillAccessor(item) {
      return item[this.fillTransform.field];
    },
  },
};
</script>
